import clsx from "clsx";
import React, { useEffect, useState, useRef } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import { useRouter } from "next/router";
import { Dialog } from "@headlessui/react";

const NewContainer = dynamic(
  import("./NewContainer").then((mod) => mod.NewContainer),
  { ssr: true }
);

const Logo = dynamic(() => import("./Logo").then((mod) => mod.Logo), {
  ssr: true,
});

const useCasesItems = {
  "By Industry": [
    { name: "Accounting Firms", href: "#" },
    { name: "Logistics", href: "#" },
    { name: "Services", href: "#" },
    { name: "Food & Beverages", href: "#" },
  ],
  "By Role": [
    { name: "Fractional CFO", href: "#" },
    { name: "Accounting Manager", href: "#" },
  ],
};

const resourcesItems = [
  { name: "Blog", href: "/blog" },
  { name: "News", href: "/news" },
  { name: "Contact Us", href: "/contact" },
  { name: "Ledgerowl vs Xero", href: "/vs-xero" },
  { name: "Ledgerowl vs Outsourcing", href: "/vs-outsourcing" },
];

export default function NewHeader({ pricingRef, navC, featureRef, howItWorksRef, teamRef }) {
  const router = useRouter();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const flatMenu = [
    { name: "How It Works", href: "/#how-it-works" },
    { name: "Why Ledgerowl", href: "/#services" },
    { name: "Pricing", href: "/pricing" },
    { name: "Resources", href: "#", dropdown: "resources" },
  ];

  const mobileLinks = [
    { name: "How It Works", href: "/#how-it-works" },
    { name: "Why Ledgerowl", href: "/#services" },
    { name: "Pricing", href: "/pricing" },
    { name: "Our Team", href: "/#team" },
    { name: "Resources", href: "/news" },
  ];

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const navigateTo = (href) => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
    if (href.startsWith("http")) {
      window.location.href = href;
    } else if (href.startsWith("/#")) {
      const id = href.replace("/#", "");
      if (router.asPath.split("#")[0] === "/") {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.location.href = href;
      }
    } else {
      router.push(href);
    }
  };

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  return (
    <header
      className={clsx(
        "fixed z-50 w-full lg:py-6 transition-all duration-500 bg-white/95 backdrop-blur-md border-b border-[rgba(0,0,0,0.06)]",
        isScrolled && "shadow-[0_2px_20px_rgba(0,0,0,0.08)]"
      )}
    >
      <NewContainer className={"lg:px-8"}>
        <nav
          className="mx-auto flex max-w-7xl items-center justify-between px-4 py-6 md:px-6 lg:px-0 lg:py-0"
          aria-label="Global"
        >
          <div className="flex lg:flex-1">
            <Link href="/" className="hover:cursor-pointer -m-1.5 p-1.5" aria-label="Home">
              <Logo className="h-6 w-auto" variant="dark" />
            </Link>
          </div>
          <div className="flex lg:hidden">
            <button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-cool-700"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="hidden lg:flex lg:gap-x-9 lg:items-center" ref={dropdownRef}>
            {flatMenu.map((item) =>
              item.dropdown ? (
                <div key={item.name} className="relative">
                  <button
                    onClick={() => toggleDropdown(item.dropdown)}
                    className="inline-flex items-center gap-1.5 font-inter text-[15px] font-medium text-gray-cool-700 hover:text-gray-cool-900 transition-colors cursor-pointer bg-transparent border-none p-0"
                  >
                    {item.name}
                    <ChevronDownIcon
                      className={clsx(
                        "w-3 h-3 text-gray-cool-500 transition-transform",
                        openDropdown === item.dropdown && "rotate-180"
                      )}
                    />
                  </button>
                  {openDropdown === item.dropdown && (
                    <div className="absolute top-full left-[-16px] mt-4 bg-white rounded-xl shadow-[0_12px_40px_rgba(0,0,0,0.12)] border border-[rgba(0,0,0,0.06)] min-w-[260px] py-3 z-50">
                      {item.dropdown === "useCases" ? (
                        <>
                          {Object.entries(useCasesItems).map(([section, items], idx) => (
                            <React.Fragment key={section}>
                              {idx > 0 && <div className="h-px bg-gray-cool-100 mx-4 my-2" />}
                              <div className="px-5 pt-2 pb-1">
                                <span className="text-[11px] font-bold uppercase tracking-[1px] text-gray-cool-500">
                                  {section}
                                </span>
                              </div>
                              {items.map((sub) => (
                                <a
                                  key={sub.name}
                                  href={sub.href}
                                  className="block px-5 py-2 text-[14px] text-gray-cool-700 hover:bg-blue-primary-500/[0.06] hover:text-blue-primary-500 rounded-lg mx-2 transition-all"
                                >
                                  {sub.name}
                                </a>
                              ))}
                            </React.Fragment>
                          ))}
                        </>
                      ) : (
                        resourcesItems.map((sub) => (
                          <a
                            key={sub.name}
                            href={sub.href}
                            className="block px-5 py-2 text-[14px] text-gray-cool-700 hover:bg-blue-primary-500/[0.06] hover:text-blue-primary-500 rounded-lg mx-2 transition-all"
                          >
                            {sub.name}
                          </a>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div
                  key={item.name}
                  onClick={() => navigateTo(item.href)}
                  className="hover:cursor-pointer font-inter text-[15px] font-medium leading-6 transition-colors text-gray-cool-700 hover:text-gray-cool-900"
                >
                  {item.name}
                </div>
              )
            )}
          </div>
          <div className="hidden lg:flex lg:flex-1 lg:justify-end">
            <div className="flex items-center gap-5">
              <Link
                href="https://app.ledgerowl.com/auth/login"
                className="font-inter text-[15px] font-medium transition-colors text-gray-cool-700 hover:text-gray-cool-900"
              >
                Login
              </Link>
              <Link
                href="/contact"
                className="font-inter items-center flex gap-[10px] rounded-full bg-blue-primary-500 hover:bg-[#005bb5] px-6 py-2.5 text-[14px] font-semibold text-base-white transition-all duration-200 hover:-translate-y-0.5"
              >
                Book a Demo
              </Link>
            </div>
          </div>
        </nav>
      </NewContainer>
      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-50" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-base-white px-6 py-6 sm:max-w-sm drop-shadow-lg">
          <div className="flex items-center justify-between">
            <Link href="/" className="-m-1.5 p-1.5">
              <div className="md:hidden">
                <Logo className="h-6 w-auto" />
              </div>
            </Link>
            <div
              className="-m-2.5 rounded-md p-2.5 text-gray-cool-700 md:justify-end cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </div>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-cool-500/10">
              <div className="space-y-2 py-6">
                {mobileLinks.map((item) => (
                  <div
                    key={item.name}
                    onClick={() => navigateTo(item.href)}
                    className="block py-2 hover:cursor-pointer font-inter text-base font-semibold leading-6 text-gray-cool-900"
                  >
                    {item.name}
                  </div>
                ))}
              </div>
              <div className="py-6">
                <div className="flex flex-col text-left gap-3">
                  <Link
                    href="https://app.ledgerowl.com/auth/login"
                    className="font-inter font-semibold text-gray-cool-700"
                  >
                    Login
                  </Link>
                  <Link
                    href="/contact"
                    className="inline-flex w-fit font-inter rounded-full bg-blue-primary-500 px-6 py-2.5 text-[14px] font-semibold text-base-white text-center"
                  >
                    Book a Demo
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  );
}
